/**
 * AI agent management — stateless orchestration for the control panel.
 *
 * These endpoints answer 200 with `success: false` when they refuse a call.
 * `apiClient`'s response interceptor already rejects any body carrying
 * `success: false`, so the checks here are a second line of defence for bodies
 * that omit the flag entirely rather than the primary guard.
 */
import { UnusableResponseError } from '../../utils/apiErrorHandler';
import { API_ENDPOINTS } from '../endpoints';
import { isPagedEnvelope } from '../admin/adminMapper';
import type { AdminPage } from '../admin/adminTypes';
import agentAdminApi from './agentAdminApi';
import {
  mapAdminAgent,
  mapAdminAgentDetail,
  mapAgentPersona,
} from './agentAdminMapper';
import type {
  AdminAgent,
  AdminAgentDetail,
  AgentPersona,
  AgentUpsertInput,
} from './agentAdminTypes';

const DEFAULT_PAGE_SIZE = 20;

export interface AgentListParams {
  userName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  isActive?: boolean | null;
  /** ISO date; the endpoint matches agents that joined on that day. */
  joiningDate?: string;
  pageNumber?: number;
  pageSize?: number;
}

/** Unset and empty-string filters are sent as explicit null, which the API
 * treats as "no filter". */
const optional = <T>(value: T | null | undefined): T | null =>
  value === undefined || value === '' ? null : (value as T | null);

/** FormData only accepts strings and blobs, and skips fields left undefined. */
const appendField = (
  form: FormData,
  name: string,
  value: string | number | undefined,
): void => {
  if (value === undefined || value === '') {
    return;
  }
  form.append(name, String(value));
};

const agentAdminService = {
  getAgents: async (params: AgentListParams = {}): Promise<AdminPage<AdminAgent>> => {
    const pageNumber = params.pageNumber ?? 1;
    const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
    const { data } = await agentAdminApi.list({
      userName: optional(params.userName),
      firstName: optional(params.firstName),
      lastName: optional(params.lastName),
      email: optional(params.email),
      isActive: optional(params.isActive),
      joiningDate: optional(params.joiningDate),
      pageNumber,
      pageSize,
      // Read requests send the device offset so joining dates come back local.
      timezoneOffset: new Date().getTimezoneOffset(),
    });

    // `agents` is nullable in the contract, so an empty page is a valid
    // response, not a failure; only a missing envelope is unusable.
    if (!isPagedEnvelope(data, 'agents')) {
      throw new UnusableResponseError(API_ENDPOINTS.AGENT.AGENTS_LIST);
    }

    const totalCount = data.totalRecords ?? 0;
    const totalPages = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0;
    return {
      items: (Array.isArray(data.agents) ? data.agents : []).map(mapAdminAgent),
      totalCount,
      pageNumber,
      pageSize,
      totalPages,
      hasNextPage: pageNumber < totalPages,
      hasPreviousPage: pageNumber > 1,
    };
  },

  getAgent: async (agentId: number): Promise<AdminAgentDetail> => {
    const { data } = await agentAdminApi.details({
      agentId,
      timezoneOffset: new Date().getTimezoneOffset(),
    });
    if (!data?.success || !data.botDetails) {
      throw new UnusableResponseError(API_ENDPOINTS.AGENT.AGENT_DETAILS);
    }
    return mapAdminAgentDetail(data.botDetails);
  },

  saveAgent: async (input: AgentUpsertInput): Promise<void> => {
    const form = new FormData();
    form.append('Action', input.action);
    appendField(form, 'AgentId', input.agentId);
    appendField(form, 'UserName', input.userName);
    appendField(form, 'Email', input.email);
    appendField(form, 'Password', input.password);
    appendField(form, 'FirstName', input.firstName);
    appendField(form, 'MiddleName', input.middleName);
    appendField(form, 'LastName', input.lastName);
    appendField(form, 'MobileNo', input.mobileNo);
    appendField(form, 'AgentPersonaId', input.agentPersonaId);
    appendField(form, 'Bio', input.bio);
    appendField(form, 'LikeFrequency', input.likeFrequency);
    appendField(form, 'CommentFrequency', input.commentFrequency);
    appendField(form, 'Gender', input.gender);
    appendField(form, 'Dob', input.dob);
    appendField(form, 'Country', input.country);
    appendField(form, 'Height', input.height);
    appendField(form, 'HeightScaleUnit', input.heightScaleUnit);
    appendField(form, 'Weight', input.weight);
    appendField(form, 'WeightScaleUnit', input.weightScaleUnit);
    appendField(form, 'StartWeight', input.startWeight);
    appendField(form, 'GoalWeight', input.goalWeight);
    if (input.photo) {
      form.append('Files', input.photo);
    }

    const { data } = await agentAdminApi.upsert(form);
    if (data && data.success === false) {
      throw new UnusableResponseError(API_ENDPOINTS.AGENT.AGENT);
    }
  },

  setAgentActive: async (agentId: number, isActive: boolean): Promise<void> => {
    const { data } = await agentAdminApi.setActive({ agentId, isActive });
    if (data && data.success === false) {
      throw new UnusableResponseError(API_ENDPOINTS.AGENT.AGENT);
    }
  },

  getPersonas: async (): Promise<AgentPersona[]> => {
    const { data } = await agentAdminApi.personas();
    if (!data || (!data.success && !Array.isArray(data.personas))) {
      throw new UnusableResponseError(API_ENDPOINTS.AGENT.PERSONA_LIST);
    }
    return (Array.isArray(data.personas) ? data.personas : []).map(mapAgentPersona);
  },
};

export default agentAdminService;
